<script setup lang="ts">
    import { computed, onMounted, ref } from 'vue';
    import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';
    import { enUS as dateFnsLocaleEn } from 'date-fns/locale/en-US';
    import { pt as dateFnsLocalePt } from 'date-fns/locale/pt';
    import { useUserStore } from '@/stores/user';
    import { useEventStore } from '@/stores/event';
    import { useDisplay, useTheme } from 'vuetify';
    import { useI18n } from 'vue-i18n';
    import { EventFeedType } from '@/constants';
    import { getCountryFlagUrl, getTeamLogoUrl } from '@/helpers';
    import { timeUntilEventKickoff } from '@/helpers/event';

    const { t } = useI18n();

    // stores
    const userStore = useUserStore();
    const eventStore = useEventStore();

    // reactive state
    const prevScore = ref<Record<any, any> | null>(null);

    const emit = defineEmits(['close-event-dialog']);

    const display = useDisplay();
    const theme = useTheme();

    onMounted(() => {
        console.log('Component %s mounted.', 'app-eventInfo');
    });

    const closeEventDialog = () => {
        emit('close-event-dialog', {});
    };

    const getPrevScore = () => prevScore.value;

    const setPrevScore = (lastChangedScore: string, lastGoalAt: number) => {
        if (prevScore.value === null || eventStore.stats!.score !== lastChangedScore) {
            prevScore.value = {
                score: eventStore.stats!.score,
                lastChangedScore,
                lastGoalAt,
            };
        }
    };

    const getScore = () => {
        //todo refactoring needed
        if (eventStore.stats === null || !eventStore.stats.score) {
            return '? - ?';
        }

        let prev = getPrevScore();
        const scoreToDisplay = eventStore.stats.score.replace('-', ' - ');
        const score = eventStore.stats.score.split('-');

        if (prev) {
            const diffTime = (Date.now() - prev.lastGoalAt) / 1000; // difference in seconds

            if (prev.score === eventStore.stats.score && diffTime > 20 && !eventStore.stats.isScoresMismatch) {
                return scoreToDisplay;
            }

            if (prev.score !== eventStore.stats.score) {
                setPrevScore(prev.score, Date.now());
            }

            if (!prev.score) {
                return scoreToDisplay;
            }

            const prevParts = prev.score !== eventStore.stats.score ? prev.score.split('-') : prev.lastChangedScore.split('-');

            if (prevParts[0] !== score[0]) {
                return '<span><span class="redColorScore' + (eventStore.stats.isScoresMismatch ? ' redText' : '') + '">' + score[0] + '</span><span> - ' + score[1] + '</span></span>';
            }

            if (prevParts[1] !== score[1]) {
                return '<span><span>' + score[0] + ' - </span><span class="redColorScore' + (eventStore.stats.isScoresMismatch ? ' redText' : '') + '">' + score[1] + '</span></span>';
            }
        }

        if (eventStore.stats.isScoresMismatch && eventStore.stats.scoreList) {
            const homeScores = eventStore.stats.scoreList.map((entry: any) => entry[1]?.split('-')[0]);
            const firstHomeScore: string = homeScores[0];
            const isHomeScoresDiff = homeScores.some((s: string) => s !== firstHomeScore);

            if (isHomeScoresDiff) {
                return '<span><span class="redColorScore redText">' + score[0] + '</span><span> - ' + score[1] + '</span></span>';
            }

            return '<span><span>' + score[0] + ' - </span><span class="redColorScore redText">' + score[1] + '</span></span>';
        }

        setPrevScore(eventStore.stats.score, 0);
        return scoreToDisplay;
    };

    const formatDballDate = (date: Date | string) => {
        if (!(date instanceof Date)) {
            date = fromZonedTime(date, 'Europe/London');
        }
        const now = new Date();
        const tomorrow = new Date();
        tomorrow.setDate(now.getDate() + 1);
        const isToday = date.toDateString() === now.toDateString();
        const isTomorrow = date.toDateString() === tomorrow.toDateString();
        let locale = dateFnsLocaleEn;
        if (userStore.locale === 'pt') {
            locale = dateFnsLocalePt;
        }
        const options = { locale };
        const timeZone = userStore.timeZone;
        if (isToday) {
            return t('event.today') + ', ' + formatInTimeZone(date, timeZone, 'H:mm', options);
        } else if (isTomorrow) {
            return t('event.tomorrow') + ', ' + formatInTimeZone(date, timeZone, 'H:mm', options);
        }
        return formatInTimeZone(date, timeZone, 'EEEE, MMM d, HH:mm', options);
    };

    const scoreWidth = computed(() => {
        if (eventStore.stats !== null && eventStore.eventFeedType === EventFeedType.Live) {
            return '120px';
        }
        if (Number(eventStore.leftUntilStart) < 1) {
            return '126px';
        }
        let leftUntilStart = Number(eventStore.leftUntilStart);
        let days = Math.floor(leftUntilStart / (60 * 24));
        let hour = Math.floor((leftUntilStart % (60 * 24)) / 60);
        let minutes = Math.floor((leftUntilStart % (60 * 24)) % 60);
        let width = 100;
        if (display.width.value < 400) {
            width += 50;
        } else if (display.width.value < 500) {
            width += 30;
        }
        if (days) {
            width += days < 10 ? 35 : 40;
        }
        if (hour) {
            width += hour < 10 ? 25 : 35;
        }
        if (minutes) {
            width += minutes < 10 ? 15 : 20;
        }
        return width + 'px';
    });
</script>

<template>
    <v-card-title
        class="pa-1"
        :class="display.width.value <= 430 ? 'py-0' : ''"
        style="min-height: 92px"
        :style="theme.name.value === 'dark' ? 'background-color: #1e1f22' : 'background-color: white'"
    >
        <v-container class="px-0 py-3" style="min-height: 97px">
            <v-row v-if="eventStore.leagueName" class="mx-0 pl-2 d-flex align-center" style="height: 26px">
                <img v-if="eventStore.iso3" :alt="eventStore.iso3" :src="getCountryFlagUrl(eventStore.iso3)" width="16px" height="16px" />
                <img v-else alt="world" :src="getCountryFlagUrl('world')" width="16px" height="16px" />
                <span class="px-1 font14 greyText font-weight-regular">
                    {{ eventStore.leagueName }}
                </span>
                <v-btn tag="a" icon variant="text" @click="eventStore.toggleLeagueFavorite(eventStore.leagueId!)" :disabled="eventStore.isLeagueProcessing(eventStore.leagueId!)">
                    <v-icon
                        size="22"
                        :color="eventStore.isLeagueFavorite(eventStore.leagueId!) ? 'favStarOn' : 'favStarOff'"
                        :icon="eventStore.isLeagueFavorite(eventStore.leagueId!) ? mdiStar : mdiStarOutline"
                    ></v-icon>
                </v-btn>
            </v-row>

            <v-row class="mx-0 mt-4">
                <v-col class="pa-0 d-flex align-center justify-center flex-row">
                    <v-btn tag="a" class="mr-2" icon variant="text" @click="eventStore.toggleTeamFavorite(eventStore.teamHomeId!)" :disabled="eventStore.isTeamProcessing(eventStore.teamHomeId!)">
                        <v-icon
                            size="22"
                            :color="eventStore.isTeamFavorite(eventStore.teamHomeId!) ? 'favStarOn' : 'favStarOff'"
                            :icon="eventStore.isTeamFavorite(eventStore.teamHomeId!) ? mdiStar : mdiStarOutline"
                        ></v-icon>
                    </v-btn>
                    <img v-if="eventStore.teamHomeLogo" :src="getTeamLogoUrl(eventStore.teamHomeLogo)" class="logo" alt="team" />
                    <img v-else src="https://cdn.sportmonks.com/images/soccer/team_placeholder.png" class="logo" alt="team" />
                </v-col>
                <v-col class="pt-4 px-0" :style="'max-width:' + scoreWidth">
                    <v-row class="ma-0 pa-0 d-flex flex-column align-center justify-center" style="z-index: 2; position: relative">
                        <span
                            v-if="eventStore.eventFeedType === EventFeedType.Live"
                            :data-tooltip="eventStore.stats!.timeTooltip"
                            :class="[!eventStore.stats!.time ? 'redText' : eventStore.stats!.time === 'FT' ? 'ft-time' : 'time', 'bold']"
                            style="position: relative"
                        >
                            {{ eventStore.stats!.displayedTime }}
                        </span>
                        <span v-else :class="display.width.value <= 945 ? (display.width.value <= 660 ? 'font10' : 'font13') : 'font13'" style="position: relative">
                            {{ formatDballDate(eventStore.eventStartedAt!) }}
                        </span>
                    </v-row>
                    <v-row class="ma-0 pa-0 pt-1 d-flex flex-column align-center justify-center" style="height: 38px; z-index: 1; position: relative" dense>
                        <span v-if="eventStore.stats !== null && eventStore.eventFeedType === EventFeedType.Live">
                            <span :data-tooltip="eventStore.stats.scoreTooltip" class="bold">
                                <span v-html="getScore()" :class="[eventStore.stats!.time === 'FT' ? 'ft-score' : 'score']"></span>
                            </span>
                        </span>
                        <span
                            v-else
                            style="padding: 0 6px !important"
                            class="date-remaining"
                            :class="[display.width.value <= 860 ? 'font11' : 'font13', Number(eventStore.leftUntilStart) < 1 ? 'orangeText' : 'greenText']"
                        >
                            {{ timeUntilEventKickoff(eventStore.leftUntilStart!) }}
                        </span>
                    </v-row>
                </v-col>
                <v-col class="pa-0 d-flex align-center justify-center flex-row">
                    <img v-if="eventStore.teamAwayLogo" :src="getTeamLogoUrl(eventStore.teamAwayLogo)" class="logo" alt="team" />

                    <img v-else src="https://cdn.sportmonks.com/images/soccer/team_placeholder.png" class="logo" alt="team" />
                    <v-btn tag="a" class="ml-2" icon variant="text" @click="eventStore.toggleTeamFavorite(eventStore.teamAwayId!)" :disabled="eventStore.isTeamProcessing(eventStore.teamAwayId!)">
                        <v-icon
                            size="22"
                            :color="eventStore.isTeamFavorite(eventStore.teamAwayId!) ? 'favStarOn' : 'favStarOff'"
                            :icon="eventStore.isTeamFavorite(eventStore.teamAwayId!) ? mdiStar : mdiStarOutline"
                        ></v-icon>
                    </v-btn>
                </v-col>
            </v-row>

            <v-row class="mx-0 mt-2">
                <v-col class="pl-7 py-0 pr-0 d-flex align-center justify-center flex-row">
                    <span class="team text-center">
                        {{ eventStore.teamHomeName }}
                    </span>
                </v-col>
                <v-col class="text-center pa-0 d-flex flex-column align-center justify-center" :style="'max-width:' + scoreWidth"></v-col>
                <v-col class="pr-7 py-0 pl-0 d-flex align-center justify-center flex-row">
                    <span class="team text-center">
                        {{ eventStore.teamAwayName }}
                    </span>
                </v-col>
            </v-row>

            <v-row v-if="eventStore.stats && (eventStore.stats.redAway || eventStore.stats.redHome)" class="mx-0 mt-0">
                <v-container fluid class="d-flex font20 font-weight-bold py-0">
                    <div class="left text-right">
                        <v-chip v-if="eventStore.stats.redHome" class="px-1 red-cards font14 white--text bold" color="#d62828" label variant="flat" size="20" style="min-width: 18px">
                            <span class="d-flex justify-center" style="width: 100%">
                                {{ eventStore.stats.redHome }}
                            </span>
                        </v-chip>
                        <span v-else>&nbsp;</span>
                    </div>
                    <div class="middle text-center px-0" :style="'width:' + scoreWidth"></div>
                    <div class="right text-left">
                        <v-chip v-if="eventStore.stats.redAway" class="px-1 red-cards font14 white--text bold" color="#d62828" label variant="flat" size="20" style="min-width: 18px">
                            <span class="d-flex justify-center" style="width: 100%">
                                {{ eventStore.stats.redAway }}
                            </span>
                        </v-chip>
                        <span v-else>&nbsp;</span>
                    </div>
                </v-container>
            </v-row>
            <v-btn
                density="default"
                tag="a"
                :icon="mdiClose"
                variant="text"
                position="absolute"
                location="end"
                @click="closeEventDialog"
                size="36"
                class="mr-2"
                :style="display.width.value <= 430 ? 'top: 30px; right: 5px' : 'top: 35px; right: 10px'"
            ></v-btn>
        </v-container>
    </v-card-title>
</template>

<style scoped>
    .logo {
        width: 75px;
        height: auto;
        object-fit: cover;
    }

    .date-remaining {
        font-size: 13px;
        text-align: center;
    }

    .left {
        width: 50%;
    }

    .right {
        width: 50%;
    }

    .font20 {
        font-size: 20px;
    }

    .score {
        font-size: 40px;
        color: #ff9900;
    }

    .ft-score {
        font-size: 32px;
    }

    .team {
        font-size: 14px;
    }

    @media only screen and (max-width: 600px) {
        .score {
            font-size: 32px;
        }

        .ft-score {
            font-size: 32px;
        }

        .team {
            font-size: 9px;
        }
    }

    @media (max-width: 380px) {
        .logo {
            width: 55px;
        }
    }

    .time {
        font-size: 14px;
    }

    .ft-time {
        font-size: 14px;
    }

    .team {
        text-overflow: ellipsis;
        white-space: nowrap;
        overflow: hidden;
    }
</style>
